import React, { useEffect, useMemo, useCallback } from 'react';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useResources } from '@platform/core/src/context/ResourceContext';
import { useMissingResourceFetcher } from '../../hooks/useMissingResourceFetcher';

// Configuration for mapping short tags to API and UI types
const TAG_DEFINITIONS = [
  { tag: 'A', apiType: 'exercise_definition', uiType: 'exercise_definition' },
  { tag: 'E', apiType: 'location_feature', uiType: 'equipment' },
  { tag: 'L', apiType: 'location', uiType: 'location' },
  { tag: 'P', apiType: 'workout_plan', uiType: 'workout_plan' },
  { tag: 'S', apiType: 'workout_session', uiType: 'workout_session' }
];

/**
 * Sub-component to handle fetching for a specific tag type.
 * Separated to ensure hooks (useMissingResourceFetcher) are called unconditionally.
 */
const ResourceTypeFetcher = ({ tag, apiType, uiType, messages, resources, onNewResources }) => {
  const requiredIds = useMemo(() => {
    const ids = new Set();
    // Match [Tag]ID[/Tag] pattern, allowing for potential whitespace around ID
    // Example: [E]0fda994f-361a...[/E]
    const regex = new RegExp(`\\[${tag}\\]\\s*([a-zA-Z0-9-]+)\\s*\\[\\/${tag}\\]`, 'g');
    
    messages.forEach(msg => {
      const content = typeof msg.content === 'string' ? msg.content : '';
      let match;
      while ((match = regex.exec(content)) !== null) {
        ids.add(match[1]);
      }
    });
    return Array.from(ids);
  }, [messages, tag]);

  // Hook to fetch IDs that are missing from the 'resources' list
  const fetched = useMissingResourceFetcher(apiType, requiredIds, resources);

  useEffect(() => {
    if (fetched.length > 0) {
      onNewResources(fetched, uiType);
    }
  }, [fetched, onNewResources, uiType]);

  return null;
};

const ChatResourceFetcher = () => {
  const { messages } = useSession();
  const { resources, setResources } = useResources();

  const handleNewResources = useCallback((newItems, uiType) => {
    if (!setResources) {
      return;
    }
    setResources(prev => {
      const prevIds = new Set(prev.map(r => String(r.id)));
      const uniqueNew = newItems.filter(r => !prevIds.has(String(r.id)));
      
      if (uniqueNew.length === 0) return prev;

      console.log(`[ChatResourceFetcher] Injecting ${uniqueNew.length} missing resources (UI Type: ${uiType}) into global context.`);
      
      // Normalize the resource type for the UI (e.g., location_feature -> equipment)
      const processed = uniqueNew.map(item => ({
          ...item,
          type: uiType,
          resource_type: uiType // Ensure consistency for viewers
      }));

      return [...prev, ...processed];
    });
  }, [setResources]);

  const recentMessages = useMemo(() => {
    if (!messages) return [];
    return messages.slice(-20);
  }, [messages]);

  if (!resources) return null;

  return (
    <>
      {TAG_DEFINITIONS.map((def) => (
        <ResourceTypeFetcher
          key={def.tag}
          tag={def.tag}
          apiType={def.apiType}
          uiType={def.uiType}
          messages={recentMessages}
          resources={resources}
          onNewResources={handleNewResources}
        />
      ))}
    </>
  );
};

export default ChatResourceFetcher;